/**
 * Multi-track timeline on a single canvas. Rows: one per layer. Segmented
 * layers draw their segments with carrier (solid, log-scaled) and beat
 * (dashed, linear 0..42 Hz) curves; noise layers draw their dB envelope;
 * ambience/voice draw span bars. Interactions: drag segment boundaries
 * (contiguity preserved — moving a boundary moves t1 of the left segment AND
 * t0 of the right one), click a segment/row to select, click the ruler to
 * seek.
 */

import type { Preset, Layer } from "../../schema/types.ts";
import { isSegmentedLayer } from "../../schema/types.ts";
import { compileRamp } from "../dsp/curves.ts";
import type { Store } from "./store.ts";

const RULER_H = 26;
const ROW_H = 76;
const LABEL_W = 148;
const MIN_SEG_LEN = 0.1;

// Calm-night-sky layer accents (matches the app's goalAccent family).
const LAYER_COLORS: Record<Layer["type"], string> = {
  binaural: "#8fa8ff",
  monaural: "#7ac8ff",
  isochronic: "#c09cff",
  noise: "#8d9ab8",
  ambience: "#6fd9c9",
  voice: "#f2b880",
};

interface DragState {
  layerId: string;
  boundary: number; // boundary between segment[boundary] and [boundary+1]
  moved: boolean;
}

export class Timeline {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private drag: DragState | null = null;
  onSeek: ((sec: number) => void) | null = null;

  constructor(
    container: HTMLElement,
    private readonly store: Store,
  ) {
    this.canvas = document.createElement("canvas");
    this.canvas.className = "timeline";
    container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext("2d")!;

    this.canvas.addEventListener("pointerdown", (e) => this.onDown(e));
    this.canvas.addEventListener("pointermove", (e) => this.onMove(e));
    this.canvas.addEventListener("pointerup", (e) => this.onUp(e));
    new ResizeObserver(() => this.draw()).observe(container);
    store.subscribe(() => this.draw());
  }

  private xToTime(x: number): number {
    const w = this.canvas.clientWidth - LABEL_W;
    const d = this.store.state.preset.meta.durationSec;
    return Math.max(0, Math.min(d, ((x - LABEL_W) / w) * d));
  }

  private timeToX(t: number): number {
    const w = this.canvas.clientWidth - LABEL_W;
    return LABEL_W + (t / this.store.state.preset.meta.durationSec) * w;
  }

  private rowAt(y: number): number {
    return Math.floor((y - RULER_H) / ROW_H);
  }

  // ------------------------------------------------------------ interaction

  private hitBoundary(x: number, y: number): DragState | null {
    const { preset } = this.store.state;
    const row = this.rowAt(y);
    const layer = preset.layers[row];
    if (!layer || !isSegmentedLayer(layer)) return null;
    for (let i = 0; i < layer.segments.length - 1; i++) {
      const bx = this.timeToX(layer.segments[i]!.t1);
      if (Math.abs(x - bx) <= 5) return { layerId: layer.id, boundary: i, moved: false };
    }
    return null;
  }

  /** Mute-button hit circle in the label column. */
  private muteCenter(row: number): { cx: number; cy: number; r: number } {
    return { cx: LABEL_W - 22, cy: RULER_H + row * ROW_H + 22, r: 9 };
  }

  private hitMute(x: number, y: number): number | null {
    const row = this.rowAt(y);
    if (row < 0 || row >= this.store.state.preset.layers.length) return null;
    const { cx, cy, r } = this.muteCenter(row);
    return Math.hypot(x - cx, y - cy) <= r + 3 ? row : null;
  }

  private onDown(e: PointerEvent): void {
    const { x, y } = this.local(e);
    const muteRow = this.hitMute(x, y);
    if (muteRow !== null) {
      const layer = this.store.state.preset.layers[muteRow];
      if (layer) this.store.toggleMute(layer.id);
      return;
    }
    const hit = this.hitBoundary(x, y);
    if (hit) {
      this.drag = hit;
      this.canvas.setPointerCapture(e.pointerId);
      return;
    }
    if (y < RULER_H && x >= LABEL_W) {
      this.onSeek?.(this.xToTime(x));
      return;
    }
    const row = this.rowAt(y);
    const layer = this.store.state.preset.layers[row];
    if (!layer) return;
    if (isSegmentedLayer(layer) && x >= LABEL_W) {
      const t = this.xToTime(x);
      const idx = layer.segments.findIndex((s) => t >= s.t0 && t < s.t1);
      if (idx >= 0) {
        this.store.select({ kind: "segment", layerId: layer.id, segIndex: idx });
        return;
      }
    }
    this.store.select({ kind: "layer", layerId: layer.id });
  }

  private onMove(e: PointerEvent): void {
    const { x, y } = this.local(e);
    if (!this.drag) {
      this.canvas.style.cursor =
        this.hitMute(x, y) !== null ? "pointer" : this.hitBoundary(x, y) ? "col-resize" : "default";
      return;
    }
    const drag = this.drag;
    const t = this.xToTime(x);
    this.store.updatePreset((p) => {
      const layer = p.layers.find((l) => l.id === drag.layerId);
      if (!layer || !isSegmentedLayer(layer)) return;
      const a = layer.segments[drag.boundary]!;
      const b = layer.segments[drag.boundary + 1]!;
      const clamped = Math.max(a.t0 + MIN_SEG_LEN, Math.min(b.t1 - MIN_SEG_LEN, t));
      const snapped = Math.round(clamped * 10) / 10; // 0.1 s grid
      a.t1 = snapped;
      b.t0 = snapped;
    });
    drag.moved = true;
  }

  private onUp(e: PointerEvent): void {
    if (this.drag) this.canvas.releasePointerCapture(e.pointerId);
    this.drag = null;
  }

  private local(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  // -------------------------------------------------------------- rendering

  draw(): void {
    const { preset, selection, positionSec } = this.store.state;
    const cssW = this.canvas.parentElement!.clientWidth;
    const cssH = RULER_H + preset.layers.length * ROW_H + 8;
    const dpr = window.devicePixelRatio || 1;
    this.canvas.style.height = `${cssH}px`;
    if (this.canvas.width !== cssW * dpr || this.canvas.height !== cssH * dpr) {
      this.canvas.width = cssW * dpr;
      this.canvas.height = cssH * dpr;
    }
    const ctx = this.ctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    this.drawRuler(cssW);
    this.drawGrid(cssW, cssH);
    preset.layers.forEach((layer, i) => this.drawRow(layer, i, cssW, selection));

    // Playhead: soft glowing line with a bright cap in the ruler.
    const px = this.timeToX(positionSec);
    ctx.save();
    ctx.strokeStyle = "rgba(232, 236, 248, 0.85)";
    ctx.lineWidth = 1.2;
    ctx.shadowColor = "rgba(143, 168, 255, 0.9)";
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.moveTo(px, 4);
    ctx.lineTo(px, cssH);
    ctx.stroke();
    ctx.fillStyle = "#e8ecf8";
    ctx.beginPath();
    ctx.arc(px, RULER_H - 7, 3, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawGrid(w: number, h: number): void {
    const ctx = this.ctx;
    const d = this.store.state.preset.meta.durationSec;
    const step = d <= 30 ? 5 : d <= 120 ? 15 : d <= 600 ? 60 : 300;
    ctx.strokeStyle = "rgba(143, 168, 255, 0.05)";
    ctx.lineWidth = 1;
    for (let t = step; t < d; t += step) {
      const x = Math.round(this.timeToX(t)) + 0.5;
      ctx.beginPath();
      ctx.moveTo(x, RULER_H);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    void w;
  }

  private drawRuler(w: number): void {
    const ctx = this.ctx;
    const d = this.store.state.preset.meta.durationSec;
    ctx.fillStyle = "rgba(13, 18, 38, 0.85)";
    ctx.fillRect(LABEL_W, 0, w - LABEL_W, RULER_H);
    ctx.strokeStyle = "rgba(38, 50, 90, 0.8)";
    ctx.beginPath();
    ctx.moveTo(LABEL_W, RULER_H - 0.5);
    ctx.lineTo(w, RULER_H - 0.5);
    ctx.stroke();
    const step = d <= 30 ? 5 : d <= 120 ? 15 : d <= 600 ? 60 : 300;
    ctx.fillStyle = "#93a0c4";
    ctx.font = "500 9.5px Outfit, system-ui";
    ctx.strokeStyle = "rgba(147, 160, 196, 0.4)";
    for (let t = 0; t <= d + 1e-9; t += step) {
      const x = this.timeToX(Math.min(t, d));
      ctx.beginPath();
      ctx.moveTo(x, RULER_H - 5);
      ctx.lineTo(x, RULER_H);
      ctx.stroke();
      const label = t >= 60 ? `${Math.floor(t / 60)}:${String(Math.round(t % 60)).padStart(2, "0")}` : `${t}s`;
      ctx.fillText(label, x + 4, RULER_H - 9);
    }
  }

  private drawRow(layer: Layer, index: number, w: number, selection: Store["state"]["selection"]): void {
    const ctx = this.ctx;
    const y = RULER_H + index * ROW_H;
    const color = LAYER_COLORS[layer.type];
    const isSelLayer =
      selection !== null && selection.kind !== "master" && selection.layerId === layer.id;

    ctx.fillStyle = index % 2 ? "rgba(14, 20, 40, 0.55)" : "rgba(17, 24, 47, 0.55)";
    ctx.fillRect(0, y, w, ROW_H);
    ctx.strokeStyle = "rgba(28, 39, 72, 0.7)";
    ctx.beginPath();
    ctx.moveTo(0, y + ROW_H - 0.5);
    ctx.lineTo(w, y + ROW_H - 0.5);
    ctx.stroke();
    ctx.fillStyle = isSelLayer && selection.kind === "layer" ? "rgba(143, 168, 255, 0.1)" : "rgba(13, 18, 38, 0.9)";
    ctx.fillRect(0, y, LABEL_W, ROW_H);
    // Layer accent dot + labels.
    ctx.fillStyle = color;
    ctx.save();
    ctx.shadowColor = color;
    ctx.shadowBlur = 6;
    ctx.beginPath();
    ctx.arc(15, y + 17, 3.4, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
    const muted = this.store.muted.has(layer.id);
    ctx.fillStyle = muted ? "#93a0c4" : "#e8ecf8";
    ctx.font = "500 12.5px Outfit, system-ui";
    ctx.fillText(layer.id, 26, y + 21);
    ctx.fillStyle = "#93a0c4";
    ctx.font = "400 10px Outfit, system-ui";
    const gain = "envelope" in layer && layer.envelope ? "envelope" : `${layer.gainDb} dB`;
    ctx.fillText(`${layer.type} · ${gain}`, 26, y + 36);

    // Mute button (DAW-style "M").
    const mute = this.muteCenter(index);
    ctx.beginPath();
    ctx.arc(mute.cx, mute.cy, mute.r, 0, Math.PI * 2);
    if (muted) {
      ctx.fillStyle = "#f2b880";
      ctx.fill();
      ctx.fillStyle = "#241503";
    } else {
      ctx.strokeStyle = "rgba(147, 160, 196, 0.45)";
      ctx.lineWidth = 1.2;
      ctx.stroke();
      ctx.fillStyle = "rgba(147, 160, 196, 0.75)";
    }
    ctx.font = "600 9.5px Outfit, system-ui";
    ctx.fillText("M", mute.cx - 3.5, mute.cy + 3.5);

    const track = { x: LABEL_W, y: y + 4, w: w - LABEL_W, h: ROW_H - 8 };
    if (muted) ctx.globalAlpha = 0.3;
    if (isSegmentedLayer(layer)) {
      this.drawSegments(layer, track, color, selection);
    } else if (layer.type === "noise") {
      this.drawEnvelope(layer.envelope ?? null, layer.gainDb, track, color);
    } else {
      // Ambience / voice: a rounded asset card spanning its active range.
      const startT = layer.type === "voice" ? (layer.startSec ?? 0) : 0;
      const x0 = this.timeToX(startT);
      const x1 = this.timeToX(this.store.state.preset.meta.durationSec);
      const cardY = track.y + track.h * 0.28;
      const cardH = track.h * 0.44;
      const grad = ctx.createLinearGradient(0, cardY, 0, cardY + cardH);
      grad.addColorStop(0, color + "2e");
      grad.addColorStop(1, color + "12");
      ctx.beginPath();
      ctx.roundRect(x0 + 1.5, cardY, x1 - x0 - 3, cardH, 7);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = color + "45";
      ctx.stroke();
      ctx.fillStyle = color;
      ctx.font = "500 10.5px Outfit, system-ui";
      const label = (layer.type === "ambience" ? `◍ ${layer.asset}` : `▸ ${layer.source}`) +
        (layer.type === "ambience" && layer.loop !== false ? "  ·  loop" : "");
      ctx.fillText(label, x0 + 10, cardY + cardH / 2 + 3.5);
    }
    ctx.globalAlpha = 1;
  }

  private drawSegments(
    layer: Extract<Layer, { segments: unknown }>,
    track: { x: number; y: number; w: number; h: number },
    color: string,
    selection: Store["state"]["selection"],
  ): void {
    const ctx = this.ctx;
    const allCarrier = layer.segments.flatMap((s) => s.carrierHz);
    const fMin = Math.min(...allCarrier) / 1.3;
    const fMax = Math.max(...allCarrier) * 1.3;
    const beatMax = 42;
    const yOfCarrier = (f: number) =>
      track.y + track.h - ((Math.log2(f) - Math.log2(fMin)) / (Math.log2(fMax) - Math.log2(fMin))) * track.h;
    const yOfBeat = (b: number) => track.y + track.h - (b / beatMax) * track.h;

    for (const [i, seg] of layer.segments.entries()) {
      const x0 = this.timeToX(seg.t0);
      const x1 = this.timeToX(seg.t1);
      const selected =
        selection?.kind === "segment" && selection.layerId === layer.id && selection.segIndex === i;

      // Segment card: rounded, subtle vertical gradient, glow when selected.
      const grad = ctx.createLinearGradient(0, track.y, 0, track.y + track.h);
      grad.addColorStop(0, color + (selected ? "3d" : "24"));
      grad.addColorStop(1, color + (selected ? "1f" : "0d"));
      ctx.beginPath();
      ctx.roundRect(x0 + 1.5, track.y + 0.5, x1 - x0 - 3, track.h - 1, 7);
      ctx.fillStyle = grad;
      ctx.fill();
      if (selected) {
        ctx.save();
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.4;
        ctx.shadowColor = color;
        ctx.shadowBlur = 10;
        ctx.stroke();
        ctx.restore();
      } else {
        ctx.strokeStyle = color + "30";
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      // Carrier (solid, glowing) and beat (dashed) curves, ~3 px sampling.
      const carrier = compileRamp(seg.curve, seg.carrierHz[0], seg.carrierHz[1], seg.t0, seg.t1);
      const beat = compileRamp(seg.curve, seg.beatHz[0], seg.beatHz[1], seg.t0, seg.t1);
      for (const [ramp, yOf, dash] of [
        [carrier, yOfCarrier, false],
        [beat, yOfBeat, true],
      ] as const) {
        ctx.save();
        ctx.beginPath();
        ctx.setLineDash(dash ? [3, 4] : []);
        ctx.strokeStyle = dash ? color + "88" : color;
        ctx.lineWidth = dash ? 1 : 1.8;
        if (!dash) {
          ctx.shadowColor = color;
          ctx.shadowBlur = 5;
        }
        const steps = Math.max(2, Math.floor((x1 - x0) / 3));
        for (let s = 0; s <= steps; s++) {
          const t = seg.t0 + ((seg.t1 - seg.t0) * s) / steps;
          const x = x0 + ((x1 - x0) * s) / steps;
          const yv = yOf(ramp.valueAt(Math.min(t, seg.t1 - 1e-9)));
          if (s === 0) ctx.moveTo(x, yv);
          else ctx.lineTo(x, yv);
        }
        ctx.stroke();
        ctx.restore();
      }

      // Draggable-boundary grip between this segment and the next.
      if (i < layer.segments.length - 1) {
        ctx.fillStyle = color + "cc";
        ctx.beginPath();
        ctx.roundRect(x1 - 1.5, track.y + track.h / 2 - 8, 3, 16, 1.5);
        ctx.fill();
      }

      // Beat label at segment center.
      ctx.fillStyle = "rgba(232, 236, 248, 0.85)";
      ctx.font = "500 10px Outfit, system-ui";
      const label =
        seg.beatHz[0] === seg.beatHz[1] ? `${seg.beatHz[0]} Hz` : `${seg.beatHz[0]} → ${seg.beatHz[1]} Hz`;
      ctx.fillText(label, (x0 + x1) / 2 - ctx.measureText(label).width / 2, track.y + 13);
    }
  }

  private drawEnvelope(
    envelope: Array<{ t: number; gainDb: number }> | null,
    gainDb: number,
    track: { x: number; y: number; w: number; h: number },
    color: string,
  ): void {
    const ctx = this.ctx;
    const d = this.store.state.preset.meta.durationSec;
    const yOf = (db: number) =>
      track.y + track.h - (Math.max(-70, Math.min(0, db)) / 70 + 1) * track.h;
    const points = envelope ?? [
      { t: 0, gainDb },
      { t: d, gainDb },
    ];
    // Filled area under the gain envelope — reads as "noise bed level".
    const bottom = track.y + track.h - 2;
    ctx.beginPath();
    ctx.moveTo(this.timeToX(0), yOf(points[0]!.gainDb));
    for (const p of points) ctx.lineTo(this.timeToX(p.t), yOf(p.gainDb));
    ctx.lineTo(this.timeToX(d), yOf(points[points.length - 1]!.gainDb));
    ctx.lineTo(this.timeToX(d), bottom);
    ctx.lineTo(this.timeToX(0), bottom);
    ctx.closePath();
    const grad = ctx.createLinearGradient(0, track.y, 0, bottom);
    grad.addColorStop(0, color + "30");
    grad.addColorStop(1, color + "08");
    ctx.fillStyle = grad;
    ctx.fill();
    // Envelope line with glow.
    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.7;
    ctx.shadowColor = color;
    ctx.shadowBlur = 5;
    ctx.moveTo(this.timeToX(0), yOf(points[0]!.gainDb));
    for (const p of points) ctx.lineTo(this.timeToX(p.t), yOf(p.gainDb));
    ctx.lineTo(this.timeToX(d), yOf(points[points.length - 1]!.gainDb));
    ctx.stroke();
    ctx.restore();
    for (const p of points) {
      ctx.fillStyle = "#e8ecf8";
      ctx.beginPath();
      ctx.arc(this.timeToX(p.t), yOf(p.gainDb), 2.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}
